import React, { useEffect, useMemo, useState } from 'react'
import { Button, EmptyHubState, MiniLoader, StatusPill } from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import {
  listSeriesForWorkspace,
  listSessionDocs,
  updateEduSession,
  type EduSession,
} from '../../../services/eduTimetableService'
import {
  mergeSessionOccurrences,
  isSessionGap,
  findFreeTeachersForSlot,
  type TimetableOccurrence,
  type TeacherCandidate,
  type TeacherLeaveRecord,
} from '../../Education/timetableHelpers'
import { QuickDelegateSheet } from '../../FieldPass/QuickDelegateSheet'
import { subscribeWorkspaceUserDirectory } from '../../../services/workspaceUserDirectory'

function getTodayIso(): string {
  const dt = new Date()
  const y = dt.getUTCFullYear()
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const d = String(dt.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function getWeekBounds(todayIso: string): { from: string; to: string } {
  const [y, mo, d] = todayIso.split('-').map(Number)
  const dt = new Date(Date.UTC(y, mo - 1, d))
  const day = dt.getUTCDay()
  const diffToMon = day === 0 ? -6 : 1 - day
  dt.setUTCDate(dt.getUTCDate() + diffToMon)
  const mon = dt.toISOString().slice(0, 10)
  dt.setUTCDate(dt.getUTCDate() + 6)
  const sun = dt.toISOString().slice(0, 10)
  return { from: mon, to: sun }
}

export interface TimetableTeacher {
  uid: string
  displayName: string
}

export interface ClassTimetablePanelProps {
  workspaceId: string
  classId?: string
  teachers?: TimetableTeacher[]
  approvedLeaves?: TeacherLeaveRecord[]
}

type ViewMode = 'TODAY' | 'WEEK'

export const ClassTimetablePanel: React.FC<ClassTimetablePanelProps> = ({
  workspaceId,
  classId,
  teachers: teachersProp,
  approvedLeaves = [],
}) => {
  const { t } = useNls()

  const [directoryTeachers, setDirectoryTeachers] = useState<TimetableTeacher[]>([])

  useEffect(() => {
    if (teachersProp !== undefined || !workspaceId) return
    const unsub = subscribeWorkspaceUserDirectory(workspaceId, (members) => {
      const eligible = members
        .filter((m) => m.role === 'OWNER' || m.role === 'ADMIN' || m.role === 'EDITOR')
        .map((m) => ({
          uid: m.uid,
          displayName: m.displayName,
        }))
      setDirectoryTeachers(eligible)
    })
    return () => unsub()
  }, [workspaceId, teachersProp])

  const teachers = teachersProp ?? directoryTeachers

  const [viewMode, setViewMode] = useState<ViewMode>('TODAY')
  const [occurrences, setOccurrences] = useState<TimetableOccurrence[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Cover assignment state
  const [coveringOcc, setCoveringOcc] = useState<TimetableOccurrence | null>(null)
  const [assigningUid, setAssigningUid] = useState<string | null>(null)
  const [coverError, setCoverError] = useState<string | null>(null)

  // Substitute pass state
  const [passOcc, setPassOcc] = useState<TimetableOccurrence | null>(null)

  const today = getTodayIso()

  const windowBounds = useMemo(() => {
    if (viewMode === 'TODAY') return { from: today, to: today }
    return getWeekBounds(today)
  }, [viewMode, today])

  const loadData = async () => {
    if (!workspaceId) return
    setLoading(true)
    setError(null)
    try {
      const allSeries = await listSeriesForWorkspace(workspaceId)
      const filtered = classId
        ? allSeries.filter((s) => s.classId === classId)
        : allSeries

      const seriesIds = filtered.map((s) => s.id).filter(Boolean) as string[]

      let savedDocs: EduSession[] = []
      if (seriesIds.length > 0) {
        // Fetch the root from series (all have same rootId for a class)
        const rootId = filtered[0]?.rootId || ''
        savedDocs = await listSessionDocs(workspaceId, rootId, windowBounds.from, windowBounds.to)
      }

      const merged = mergeSessionOccurrences(
        filtered,
        savedDocs,
        [],
        windowBounds.from,
        windowBounds.to,
      )

      setOccurrences(merged)    } catch (err: unknown) {
      setError((err as Error)?.message || 'Failed to load timetable')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, classId, viewMode])

  const visibleOccurrences = useMemo(() => {
    return occurrences.filter((occ) => occ.status !== 'CANCELLED')
  }, [occurrences])

  const freeTeachersForCover = useMemo(() => {
    if (!coveringOcc) return []
    const slot = {
      date: coveringOcc.date,
      startTime: coveringOcc.startTime,
      durationMin: coveringOcc.durationMin,
      excludeTeacherUids: coveringOcc.teacherUids,
    }
    const candidates: TeacherCandidate[] = teachers.map((t) => ({
      uid: t.uid,
      displayName: t.displayName,
    }))
    return findFreeTeachersForSlot(slot, candidates, occurrences, approvedLeaves)
  }, [coveringOcc, teachers, occurrences, approvedLeaves])

  const handleAssignSubstitute = async (occ: TimetableOccurrence, substituteUid: string) => {
    setAssigningUid(substituteUid)
    setCoverError(null)
    try {
      await updateEduSession({
        workspaceId,
        seriesId: occ.seriesId,
        date: occ.date,
        action: 'SET_SUBSTITUTE',
        substituteUid,
      })
      setCoveringOcc(null)
      await loadData()
    } catch (err: unknown) {
      setCoverError((err as Error)?.message || 'Failed to assign substitute')
    } finally {
      setAssigningUid(null)
    }
  }

  const passTargets = useMemo(() => {
    if (!passOcc) return []
    return teachers.map((teacher) => ({
      id: teacher.uid,
      title: teacher.displayName,
    }))
  }, [passOcc, teachers])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 16px' }}>
        <MiniLoader />
      </div>
    )
  }

  return (
    <div
      className="classTimetablePanel"
      style={{ padding: '12px 16px' }}
      data-testid="class-timetable-panel"
    >
      {/* Today / Week toggle */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
        <Button
          size="sm"
          variant={viewMode === 'TODAY' ? 'primary' : 'secondary'}
          onClick={() => setViewMode('TODAY')}
          data-testid="timetable-toggle-today"
        >
          {t('NLSEduMobileTimetableToday')}
        </Button>
        <Button
          size="sm"
          variant={viewMode === 'WEEK' ? 'primary' : 'secondary'}
          onClick={() => setViewMode('WEEK')}
          data-testid="timetable-toggle-week"
        >
          {t('NLSEduMobileTimetableWeek')}
        </Button>
      </div>

      {error && (
        <div style={{ color: 'var(--danger)', marginBottom: '12px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {visibleOccurrences.length === 0 ? (
        <EmptyHubState
          title={t('NLSEduMobileTimetableEmptyTitle')}
          description={t('NLSEduMobileTimetableEmptyBody')}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} data-testid="timetable-list">
          {visibleOccurrences.map((occ) => {
            const gap = isSessionGap(occ, approvedLeaves)
            const isCovering = coveringOcc?.id === occ.id
            return (
              <div
                key={occ.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '10px',
                  border: gap
                    ? '1px solid var(--warning)'
                    : '1px solid var(--border-subtle)',
                  backgroundColor: 'var(--surface-card)',
                }}
                data-testid={`timetable-occ-${occ.id}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '14px' }}>
                      {occ.date} · {occ.startTime}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {occ.durationMin} min
                    </div>
                  </div>
                  {gap ? (
                    <StatusPill tone="warning">{t('NLSEduMobileTimetableGapLabel')}</StatusPill>
                  ) : occ.substituteUid ? (
                    <StatusPill tone="info">{t('NLSEduMobilePillCover')}</StatusPill>
                  ) : (
                    <StatusPill tone="success">{t('NLSEduMobileTimetableCovered')}</StatusPill>
                  )}
                </div>

                {gap && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setCoveringOcc(isCovering ? null : occ)}
                      data-testid={`find-cover-btn-${occ.id}`}
                    >
                      {t('NLSEduMobileTimetableFindCover')}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setPassOcc(occ)}
                      data-testid={`issue-pass-btn-${occ.id}`}
                    >
                      {t('NLSEduMobileTimetableIssuePass')}
                    </Button>
                  </div>
                )}

                {/* Cover picker */}
                {isCovering && (
                  <div
                    style={{ marginTop: '8px', borderTop: '1px solid var(--border-subtle)', paddingTop: '8px' }}
                    data-testid={`cover-picker-${occ.id}`}
                  >
                    {coverError && (
                      <div style={{ color: 'var(--danger)', fontSize: '12px', marginBottom: '6px' }}>
                        {coverError}
                      </div>
                    )}
                    {freeTeachersForCover.length === 0 ? (
                      <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                        {t('NLSEduMobileTimetableNoFreeTeachers')}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {freeTeachersForCover.map((teacher) => (
                          <Button
                            key={teacher.uid}
                            size="sm"
                            variant="secondary"
                            disabled={assigningUid !== null}
                            onClick={() => handleAssignSubstitute(occ, teacher.uid)}
                            data-testid={`assign-cover-${teacher.uid}`}
                          >
                            {teacher.displayName}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Substitute pass sheet */}
      {passOcc && (
        <QuickDelegateSheet
          open={Boolean(passOcc)}
          workspaceId={workspaceId}
          targets={passTargets}
          onClose={() => setPassOcc(null)}
        />
      )}
    </div>
  )
}
